import { useEffect, useMemo, useState } from "react";
import emissionViewStore from "../../../../Store/marco/emissionViewStore";
import { emissionFactorAPI } from "../../../API/emissionFactorAPI";
import { shouldShow } from "../utils/shouldShow";
import { flattenEAFormData } from "../utils/toRequestBody";
import { buildParamValue } from "../utils/buildParamValue";
import { buildPrefixByField } from "../utils/buildPrefixByField";
import { categoryDtoMap } from "../../../services/categoryDtoMap";
import { buildCustomEFDropdownGroups } from "../../../../EmissionFactorManagement/services/customEFSharing";

//////// 自訂 Hook：取得 EF Schema 中各 select 欄位的 options
export const useEFSelectOptions = ({
  EFSchema,
  formData,
  showAddCustomEFModal,
  cat,
  category,
  EAFormData,
}) => {
  const store = emissionViewStore;
  const { landEAFormData } = store;
  const { _handleSetEmissionActivityModalLoading } = store;

  const [optionsMap, setOptionsMap] = useState({});

  //////// 3.7 Walking / Bicycle → 只能選 Custom Emission Factor
  const WALKING_BICYCLE_IDS = [33010, 33011];

  // 將所有需要追蹤的 EAFormData 字段集中計算
  const eaFormDeps = useMemo(() => {
    const flatEA = flattenEAFormData(EAFormData);
    const landFlat = flattenEAFormData(landEAFormData);
    return {
      selectedCom: JSON.stringify(flatEA.selectedCom || flatEA.companyId || []),
      calculationId: flatEA.calculationId,
      activityId: flatEA.activityId,
      isTransport: landFlat.isTransport,
      isBiomassActivity: flatEA.isBiomassActivity,
      isBiomassSeparate: flatEA.isBiomassSeparate,
    };
  }, [EAFormData, landEAFormData]);

  const depsKey = useMemo(() => {
    return (EFSchema?.fields || [])
      .filter((f) => f.type === "select")
      .map((f) => {
        const param = buildParamValue(formData, f);

        const shown = shouldShow(
          f,
          formData,
          store[f?.specialDataShouldIf?.formData],
        )
          ? 1
          : 0;

        return `${f.key}:${String(param)}:${shown}`;
      })
      .join("|");
  }, [EFSchema, formData, eaFormDeps.selectedCom]);

  // 🔹 單一欄位 options 取得
  const fetchOptionsByField = async (f, flatEA) => {
    const api =
      f.optionsAPI === "emissionLevel"
        ? store.getEFDropdownData
        : emissionFactorAPI[f.optionsAPI];

    if (typeof api !== "function") return [f.key, []];

    try {
      if (f.custom) {
        const list = await api(
          flatEA.companyId,
          categoryDtoMap[store.selectedTab],
          store.standardId,
        );
        const currentValue = flattenEAFormData(formData)?.[f.key];
        const flatFormData = flattenEAFormData(formData);
        return [
          f.key,
          buildCustomEFDropdownGroups(list || [], currentValue, {
            isUnavailable: Boolean(flatFormData.customEFUnavailable),
            label: flatFormData.unavailableCustomEFName,
          }),
        ];
      }

      const paramValue = buildPrefixByField(EFSchema, formData, f, flatEA);

      let list = await api(
        f.optionsAPI === "emissionLevel" ? cat : category,
        paramValue,
        flatEA,
      );

      //////// 3.7 Walking / Bicycle：Data Source 只顯示 Custom Emission Factor
      if (
        f.optionsAPI === "emissionFactorDropdown" &&
        WALKING_BICYCLE_IDS.includes(Number(flatEA.activityId))
      ) {
        list = (list || []).filter((opt) => String(opt.value) === "2");
      }

      return [f.key, list || []];
    } catch (err) {
      console.error("API call failed:", f.key, err);
      return [f.key, []];
    }
  };

  // 🔹 主 effect：負責 orchestration
  useEffect(() => {
    if (!EFSchema || showAddCustomEFModal) return;

    const run = async () => {
      _handleSetEmissionActivityModalLoading(true);
      setOptionsMap({});

      const flatEA = flattenEAFormData(EAFormData);

      const selects = (EFSchema.fields || []).filter(
        (f) =>
          f.type === "select" &&
          shouldShow(f, formData, store[f?.specialDataShouldIf?.formData]),
      );

      try {
        const results = await Promise.allSettled(
          selects.map((f) => fetchOptionsByField(f, flatEA)),
        );

        const pairs = results
          .filter((r) => r.status === "fulfilled")
          .map((r) => r.value);

        setOptionsMap(Object.fromEntries(pairs));
      } finally {
        _handleSetEmissionActivityModalLoading(false);
      }
    };

    run();
  }, [
    depsKey,
    showAddCustomEFModal,
    eaFormDeps.calculationId,
    eaFormDeps.activityId,
    eaFormDeps.isTransport,
    eaFormDeps.isBiomassActivity,
    eaFormDeps.isBiomassSeparate,
    eaFormDeps.selectedCom,
  ]);

  return { optionsMap };
};
